import {stripHtml} from 'string-strip-html';
import ObjectUtils from '@spothero/utils/object';
import {spotheroGet} from 'api';
import {getTransientPricingAlgorithms} from 'api/rates/rating-rule-groups';
import {sendSentryException} from 'utils/error-handling';

/**
 * Formats the facility details response
 *
 * @function formatFacilityDetailsResponse
 * @param {number} id - The facility id
 * @param transientPricingAlgorithms
 * @param {TFacilityDetailsResponse} data - The seller details data for a single facility
 * @returns {TFacility} The formatted facility details
 *
 * @memberof module:api/facilities/dto
 */
const formatFacilityDetailsResponse = (
    id,
    transientPricingAlgorithms,
    data
) => {
    const formattedData = ObjectUtils.camelizeKeys(data);

    const response = {
        id,
        transientPricingAlgorithms,
        ...formattedData,

        transientRates:
            formattedData.transientRates.map((rate) => ({
                ...rate,
                ends: rate.ends || 'Forever',
                type: 'transient',
            })) || [],
        monthlyRates:
            formattedData.monthlyRates.map((rate) => ({
                ...rate,
                ends: rate.ends || 'Forever',
                type: 'monthly',
                description: stripHtml(rate.newDescription).result,
            })) || [],
        eventRates:
            formattedData.eventRates.map((rate) => ({
                ...rate,
                ends: rate.ends || 'Forever',
                type: 'event',
            })) || [],
        airportRates:
            formattedData?.airportRates?.map((rate) => ({
                ...rate,
                starts: rate.startDay,
                ends: rate.endDay || 'Forever',
                type: 'airport',
            })) || [],
    };

    return response;
};

/**
 * Gets the facility details for the given facility id.
 * @param {number} facilityId - The facility id.
 * @returns {Promise<TFacilityDetailsResponse>}
 * @throws {Error}
 *
 * @see '/api/v1/facilities/{facilityId}/seller-details/'
 */
const getFacilityDetails = async (facilityId) => {
    try {
        const {
            data: {data},
        } = await spotheroGet(`facilities/${facilityId}/seller-details/`);

        const transientPricingAlgorithmsResponse =
            await getTransientPricingAlgorithms([facilityId]);

        const {transientPricingAlgorithms} =
            transientPricingAlgorithmsResponse[facilityId];

        const formattedResponse = formatFacilityDetailsResponse(
            facilityId,
            transientPricingAlgorithms,
            data
        );

        return formattedResponse;
    } catch (error) {
        // console.error(error);
        sendSentryException(error);

        throw error;
    }
};

export default getFacilityDetails;
